import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { formatDateTimeEU } from '../utils/dateFormat';

const { FiMail, FiPlus, FiRefreshCw, FiEdit2, FiTrash2, FiX, FiSave, FiInfo, FiTag } = FiIcons;

const TEMPLATE_TYPES = [
  { value: 'custom', label: 'Custom Message' },
  { value: 'payment_instructions', label: 'Payment Instructions' },
  { value: 'event_details', label: 'Event Details' },
];

function emptyForm() {
  return {
    id: null,
    intake_group_id: '',
    title: '',
    subject: '',
    body: '',
    template_type: 'custom',
    active: true,
  };
}

export default function EmailTemplates() {
  const { session } = useAuth();
  const { tenantId } = useTenant();
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');

  const loadAll = async () => {
    if (!tenantId || !session?.token) return;
    try {
      setLoading(true);
      setError('');
      const [templateData, groupData] = await Promise.all([
        spformsApi.emailTemplatesList(session.token, tenantId),
        spformsApi.intakeGroupsList(tenantId),
      ]);
      setTemplates(templateData);
      setGroups(groupData);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [tenantId, session?.token]);

  const openCreate = () => {
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (template) => {
    setForm({
      id: template.id,
      intake_group_id: template.intake_group_id || '',
      title: template.title,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type,
      active: template.active,
    });
    setShowModal(true);
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete template "${template.title}"?`)) return;
    try {
      await spformsApi.emailTemplateDelete(session.token, template.id, tenantId);
      loadAll();
    } catch (err) {
      alert(err.message || 'Failed to delete template');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const payload = { ...form, tenant_id: tenantId };
      if (!payload.intake_group_id) payload.intake_group_id = null;

      if (form.id) {
        await spformsApi.emailTemplateUpdate({
          ...payload,
          token: session.token,
        });
      } else {
        await spformsApi.emailTemplateCreate({
          ...payload,
          token: session.token,
        });
      }
      setShowModal(false);
      loadAll();
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter"> Email Templates </h1>
          <p className="text-gray-500 font-medium tracking-tight"> Manage reusable messages and instructions for participants. </p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadAll} className="w-12 h-12 bg-white border rounded-2xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm" >
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={openCreate} className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-3" >
            <SafeIcon icon={FiPlus} /> New Template
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700 flex items-center gap-3"> {error} </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 rounded-[2.5rem] bg-white border-2 border-gray-100 animate-pulse" />
          ))
        ) : templates.length === 0 ? (
          <div className="col-span-full py-20 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-gray-200">
            <div className="text-lg font-bold text-gray-400">No email templates found.</div>
            <button onClick={openCreate} className="mt-4 text-indigo-600 font-black uppercase text-xs tracking-widest hover:underline" > Create your first template </button>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="group relative bg-white border-2 border-gray-100 rounded-[2.5rem] p-8 hover:border-indigo-200 transition-all shadow-sm" >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <SafeIcon icon={FiMail} size={24} />
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(template)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-indigo-600" > <SafeIcon icon={FiEdit2} /> </button>
                  <button onClick={() => handleDelete(template)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600" > <SafeIcon icon={FiTrash2} /> </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-black text-gray-900 tracking-tight line-clamp-1">{template.title}</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-gray-100 text-gray-500 rounded-md"> {template.template_type} </span>
                    {!template.active && ( <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-red-50 text-red-600 rounded-md"> Inactive </span> )}
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Subject</div>
                  <p className="text-sm font-bold text-gray-600 line-clamp-1">{template.subject}</p>
                </div>

                <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-400"> Updated {formatDateTimeEU(template.updated_at)} </span>
                  {template.intake_group_id && (
                    <div className="flex items-center gap-1 text-[10px] font-black text-indigo-600 uppercase tracking-tighter bg-indigo-50 px-2 py-0.5 rounded">
                      <SafeIcon icon={FiTag} size={10} /> Specific Event
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b flex items-center justify-between bg-gray-50/50">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight"> {form.id ? 'Edit Template' : 'New Template'} </h2>
              <button onClick={() => setShowModal(false)} className="w-10 h-10 flex items-center justify-center bg-gray-200/50 rounded-full" > <SafeIcon icon={FiX} /> </button>
            </div>
            
            <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-10 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Template Title (Internal)</label>
                  <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-bold outline-none focus:border-indigo-500" placeholder="e.g., Payment Pending Instructions" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Template Type</label>
                  <select value={form.template_type} onChange={(e) => setForm({ ...form, template_type: e.target.value })} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-bold outline-none focus:border-indigo-500" >
                    {TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Event Specific (Optional)</label>
                  <select value={form.intake_group_id} onChange={(e) => setForm({ ...form, intake_group_id: e.target.value })} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-bold outline-none focus:border-indigo-500" >
                    <option value="">Global (All Events)</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-4 pt-8">
                  <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-6 h-6 rounded border-2 border-indigo-300 text-indigo-600" />
                  <label htmlFor="active" className="text-sm font-black text-gray-700 uppercase tracking-widest">Active & Usable</label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Email Subject</label>
                <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-bold outline-none focus:border-indigo-500" placeholder="e.g., Action Required: Complete your registration" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Email Body / Message</label>
                  <div className="group relative">
                    <SafeIcon icon={FiInfo} className="text-gray-400" />
                    <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-[10px] p-4 rounded-xl shadow-xl z-10">
                      <p className="font-black uppercase tracking-widest mb-2 border-b border-white/10 pb-2">Available Placeholders</p>
                      <ul className="space-y-1 font-mono">
                        <li>&#123;&#123;participant_name&#125;&#125;</li>
                        <li>&#123;&#123;event_title&#125;&#125;</li>
                        <li>&#123;&#123;tenant_name&#125;&#125;</li>
                        <li>&#123;&#123;tenant_email&#125;&#125;</li>
                        <li>&#123;&#123;amount_due&#125;&#125;</li>
                        <li>&#123;&#123;remaining_balance&#125;&#125;</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <textarea required rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-medium outline-none focus:border-indigo-500 whitespace-pre-wrap" placeholder="Write your message here..." />
              </div>

              <div className="p-6 bg-indigo-50/50 rounded-2xl space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Quick Placeholders</h4>
                <div className="flex flex-wrap gap-2">
                  {['{{participant_name}}', '{{event_title}}', '{{tenant_name}}', '{{amount_due}}'].map(tag => (
                    <button key={tag} type="button" onClick={() => setForm({ ...form, body: form.body + tag })} className="px-3 py-1 bg-white border border-indigo-100 rounded-lg text-[10px] font-mono text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors" > {tag} </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-8 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-8 py-4 text-xs font-black text-gray-400 uppercase tracking-widest" > Cancel </button>
                <button type="submit" disabled={saving} className="px-12 py-4 bg-indigo-600 text-white rounded-[1.5rem] text-xs font-black uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-60 flex items-center gap-3" >
                  <SafeIcon icon={saving ? FiRefreshCw : FiSave} className={saving ? 'animate-spin' : ''} /> {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}